import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { fetchMarketGroupByHandle, keys, type MarketGroup } from "@/lib/queries";
import { useWatchlist, useWatchlistLoading } from "@/lib/useWatchlist";
import { MarketCard } from "@/components/markets/MarketCard";
import { MarketCardSkeleton } from "@/components/markets/MarketCardSkeleton";
import { EmptyState, Button } from "@/components/ui";
import { ContentTopBar } from "@/components/layout/ContentTopBar";
import { Bookmark } from "@/icons";
import { buildHead } from "@/lib/seo";
import { useChannelTickers } from "@/hooks/useChannelTickers";

export const Route = createFileRoute("/_app/watchlist")({
  // Personal surface: real title, but kept out of search indexes.
  head: () =>
    buildHead({
      path: "/watchlist",
      title: "Your watchlist",
      description: "The markets you're keeping an eye on.",
      noindex: true,
    }),
  component: WatchlistPage,
});

function WatchlistPage() {
  const { t } = useTranslation("discover");
  const navigate = useNavigate();
  const ids = useWatchlist();
  const listLoading = useWatchlistLoading();

  // One query per saved handle (shares the market-detail cache, so opening a
  // saved market is instant). Client-side list — no watchlist backend yet.
  const results = useQueries({
    queries: ids.map((id) => ({ queryKey: keys.marketDetail(id), queryFn: () => fetchMarketGroupByHandle(id) })),
  });
  const groups = useMemo(
    () => results.map((r) => r.data).filter((g): g is MarketGroup => !!g && g.markets.length > 0),
    [results],
  );
  const loading = results.some((r) => r.isLoading);
  // Show skeletons while the server list loads (logged-in) or while the saved
  // handles resolve — never flash the empty state before we know what's saved.
  const showSkeletons = listLoading || (loading && !groups.length);

  const marketIds = useMemo(() => groups.flatMap((g) => g.markets.map((m) => m.market_id)), [groups]);
  // SM-483: priced from the ACTIVE MODE's books. Watch/unwatch changes the
  // id set → new key; keepPrevious holds the shown prices up meanwhile.
  const { tickerFor } = useChannelTickers(marketIds, {
    refetchInterval: 30_000,
    keepPrevious: true,
  });

  return (
    <div className="py-2 lg:pt-0">
      <ContentTopBar className="-mx-4 mb-4 px-4 lg:-mx-6 lg:-mt-4 lg:px-6" divider>
        <div className="flex min-w-0 shrink-0 items-center gap-2.5">
          <Bookmark className="h-6 w-6 shrink-0 text-fg" />
          <div className="min-w-0">
            <h1 className="font-display text-xl font-semibold leading-7 text-fg">{t("common:nav.watchlist")}</h1>
            <p className="mt-0.5 truncate text-sm leading-5 text-fg-muted">
              {listLoading ? t("loading") : t("watchlist.saves", { count: ids.length })}
            </p>
          </div>
        </div>
      </ContentTopBar>

      {showSkeletons ? (
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2 xl:gap-4 2xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <MarketCardSkeleton key={i} imageLed />
          ))}
        </div>
      ) : ids.length === 0 ? (
        <EmptyState
          icon={<Bookmark />}
          title={t("watchlist.noSavedMarkets")}
          description={t("watchlist.saveHint")}
          action={<Button onClick={() => navigate({ to: "/" })}>{t("watchlist.browseMarkets")}</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2 xl:gap-4 2xl:grid-cols-4">
          {groups.map((g) => (
            <MarketCard key={g.id} group={g} tickerFor={tickerFor} imageLed />
          ))}
        </div>
      )}
    </div>
  );
}
